// Редът „Общо" — `<tfoot>`, не последен ред на `<tbody>`.
//
// В клиентски режим сумира ВСИЧКИ филтрирани редове, не само страницата (това
// е разликата спрямо antd `Table.Summary`, който получава само `pageData` и
// затова показва сумата на видимото). В сървърен режим тоталите идват от BE —
// страницата няма как да сумира период, който не е зареден.

import { useTranslation } from "react-i18next";
import type { Table } from "@tanstack/react-table";

import { DataCell } from "./DataCell";
import type { Density } from "./types";

const DENSITY_CELL: Record<Density, string> = {
  compact: "h-[38px] px-3",
  normal: "h-[42px] px-3",
  roomy: "h-[48px] px-4",
};

type Props<TData> = {
  table: Table<TData>;
  density: Density;
  serverTotals?: Record<string, number>;
  rowCount: number;
};

export const TotalsRow = <TData,>({
  table,
  density,
  serverTotals,
  rowCount,
}: Props<TData>) => {
  const { t } = useTranslation();

  const columns = table.getVisibleLeafColumns();
  const hasTotals = columns.some((column) => column.columnDef.meta?.total);
  if (!hasTotals || rowCount === 0) return null;

  const filteredRows = table.getFilteredRowModel().rows;

  // Първата колона, в която „Общо · N" има място: пропускаме точката за статус
  // и всяка друга колона без собствено заглавие.
  const labelColumnId =
    columns.find((column) => column.columnDef.meta?.cell?.kind !== "dot")?.id ??
    columns[0]?.id;

  return (
    <tfoot>
      <tr className="border-t-2 border-shade-2 bg-panel">
        {columns.map((column) => {
          const meta = column.columnDef.meta;
          // Колоната със собствен рендер описва тотала си в `totalCell`.
          const totalCell = meta?.totalCell ?? meta?.cell;
          const align =
            meta?.align ??
            (totalCell && ["num", "money", "pct", "bar"].includes(totalCell.kind)
              ? "right"
              : "left");

          const cellClass = `${DENSITY_CELL[density]} whitespace-nowrap leading-[normal] ${
            align === "right" ? "text-right" : "text-left"
          }`;

          if (column.id === labelColumnId) {
            return (
              <th key={column.id} scope="row" className={cellClass}>
                <span className="text-[12.5px] font-bold text-ink">
                  {t("dataTable.totalsLabel", { count: rowCount })}
                </span>
              </th>
            );
          }

          if (!meta?.total) {
            return <td key={column.id} className={cellClass} />;
          }

          const value =
            serverTotals !== undefined
              ? (serverTotals[column.id] ?? 0)
              : meta.total === "count"
                ? filteredRows.length
                : filteredRows.reduce((sum, row) => {
                    const raw = row.getValue(column.id);
                    const parsed = typeof raw === "number" ? raw : Number(raw);
                    return sum + (Number.isFinite(parsed) ? parsed : 0);
                  }, 0);

          return (
            <td key={column.id} className={cellClass}>
              <span className="flex justify-end">
                {totalCell ? (
                  <DataCell
                    options={
                      totalCell.kind === "money"
                        ? { ...totalCell, strong: true }
                        : totalCell
                    }
                    value={value}
                  />
                ) : (
                  <span className="font-pos-mono text-[12.5px] font-bold tabular-nums text-ink">
                    {value}
                  </span>
                )}
              </span>
            </td>
          );
        })}
      </tr>
    </tfoot>
  );
};
